"use strict";
//
// Copyright © 2025 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AnalyticsCollectorProvider = void 0;
const analytics_collector_1 = require("@hcengineering/analytics-collector");
const platform_1 = require("@hcengineering/platform");
const presentation_1 = __importDefault(require("@hcengineering/presentation"));
const rpc_1 = require("@hcengineering/rpc");
const snappyjs_1 = require("snappyjs");
const utils_1 = require("./utils");
const rpcHandler = new rpc_1.RPCHandler();
class AnalyticsCollectorProvider {
    collectIntervalMs = 5000;
    maxRetries = 3;
    maxBatchSize = 100;
    maxBatchSizeBytes = 5 * 1024 * 1024; // 5MB
    // Cap in-memory queue. Beyond this we drop oldest events to keep RAM bounded
    // when the network is offline or the server is rejecting batches.
    maxQueueSize = 5000;
    events = [];
    // Measurements fold into one event per name+labels: a find-heavy run produced 61k single-value
    // events, more than the transactor logged operations.
    metricBuckets = new Map();
    collectTimer = null;
    sending = false;
    url = '';
    email = undefined;
    anonymousId = '';
    isAuthenticated = false;
    data = null;
    init(config) {
        if (config.ANALYTICS_COLLECTOR_URL == null)
            return false;
        this.url = config.ANALYTICS_COLLECTOR_URL;
        if (this.url !== undefined && this.url !== '' && this.url !== null) {
            this.initializeAnonymousId();
            this.startCollectionTimer();
            this.registerExceptionHandlers();
            return true;
        }
        return false;
    }
    initializeAnonymousId() {
        this.anonymousId = this.generateAnonymousId();
    }
    registerExceptionHandlers() {
        // A page that closes before the 5s timer would otherwise drop its whole batch.
        window.addEventListener('pagehide', () => {
            void this.sendEvents();
        });
        // Capture unhandled errors
        window.addEventListener('error', (event) => {
            this.handleError(event.error ?? new Error(event.message));
        });
        // Capture unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            this.handleError(event.reason instanceof Error ? event.reason : new Error(String(event.reason)));
        });
    }
    generateAnonymousId() {
        return 'anon_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
    }
    startCollectionTimer() {
        if (this.collectTimer != null)
            clearInterval(this.collectTimer);
        this.collectTimer = setInterval(() => {
            void this.sendEvents();
        }, this.collectIntervalMs);
    }
    stopCollectionTimer() {
        if (this.collectTimer != null) {
            clearInterval(this.collectTimer);
            this.collectTimer = null;
        }
    }
    async sendEvents() {
        if (this.sending)
            return;
        this.drainMetrics();
        if (this.events.length === 0)
            return;
        const token = (0, platform_1.getMetadata)(presentation_1.default.metadata.Token) ?? '';
        if (token === '')
            return;
        this.sending = true;
        // Atomically dequeue: splice replaces the previous non-atomic
        // createBatches+length=0 pair, which could race with a concurrent
        // invocation and lose events.
        const pending = this.events.splice(0);
        const batches = this.createBatches(pending);
        try {
            for (const batch of batches) {
                try {
                    // Wrap batch as an RPC-style request so the server can readRequest() it
                    // with the existing RPCHandler. msgpack + snappy reduce wire size ~14x
                    // for typical telemetry batches versus uncompressed JSON.
                    const encoded = rpcHandler.serialize({ method: 'collect', params: [batch] }, true);
                    const compressed = (0, snappyjs_1.compress)(encoded);
                    const response = await fetch(`${this.url}/collect`, {
                        method: 'POST',
                        headers: {
                            Authorization: 'Bearer ' + token,
                            'Content-Type': 'application/octet-stream',
                            // Custom header avoids Express body-parser rejecting standard
                            // Content-Encoding=snappy with UnsupportedMediaTypeError.
                            'X-Encoding': 'snappy'
                        },
                        body: compressed
                    });
                    if (!response.ok) {
                        // Drop on auth/permission/payload errors - retrying is futile.
                        // Retry only transient (5xx, 408, 429).
                        const transient = response.status >= 500 || response.status === 408 || response.status === 429;
                        if (transient) {
                            this.handleFailedEvents(batch);
                        }
                    }
                }
                catch (err) {
                    // Network error - retry with backoff bound by maxRetries.
                    this.handleFailedEvents(batch);
                }
            }
        }
        finally {
            this.sending = false;
        }
    }
    createBatches(events) {
        const batches = [];
        let currentBatch = [];
        let currentBatchSize = 0;
        for (const event of events) {
            const eventSize = JSON.stringify(event).length;
            if (currentBatch.length >= this.maxBatchSize ||
                (currentBatchSize + eventSize > this.maxBatchSizeBytes && currentBatch.length > 0)) {
                batches.push(currentBatch);
                currentBatch = [];
                currentBatchSize = 0;
            }
            currentBatch.push(event);
            currentBatchSize += eventSize;
        }
        if (currentBatch.length > 0) {
            batches.push(currentBatch);
        }
        return batches;
    }
    handleFailedEvents(failedEvents) {
        const eventsToRetry = [];
        failedEvents.forEach((event) => {
            event.retryCount = (event.retryCount ?? 0) + 1;
            if (event.retryCount <= this.maxRetries) {
                eventsToRetry.push(event);
            }
        });
        this.events.unshift(...eventsToRetry);
    }
    addEvent(eventType, properties = {}, eventName, overrideDistinctId) {
        const currentId = overrideDistinctId ?? (this.isAuthenticated && this.email != null ? this.email : this.anonymousId);
        const baseProperties = {
            ...properties,
            analytics_collector: true,
            $anonymous_id: this.anonymousId,
            $is_identified: this.isAuthenticated
        };
        let eventMetadata = {};
        // Lightweight events skip the heavy per-event metadata harvest:
        // - Metric: backend only needs {metric, value, labels}.
        // - Error: backend only needs {error_message, error_type, error_stack}.
        // The metadata block (~1KB UA/screen/timezone/etc.) duplicates per-event
        // and is recoverable from setUser/setTag at session start.
        const skipMeta = eventType === analytics_collector_1.AnalyticEventType.Metric || eventType === analytics_collector_1.AnalyticEventType.Error;
        try {
            eventMetadata = skipMeta ? { ...baseProperties } : { ...baseProperties, ...(0, utils_1.collectEventMetadata)(baseProperties) };
        }
        catch (err) {
            eventMetadata = { ...baseProperties };
        }
        if (eventType === analytics_collector_1.AnalyticEventType.CustomEvent && eventName != null && eventName !== '') {
            eventMetadata.event = eventName;
        }
        if (this.data != null) {
            for (const key in this.data) {
                if (Object.prototype.hasOwnProperty.call(this.data, key)) {
                    const value = this.data[key];
                    eventMetadata[key] = value;
                }
            }
        }
        const event = {
            event: eventType,
            properties: eventMetadata,
            timestamp: Date.now(),
            distinct_id: currentId
        };
        this.events.push(event);
        if (this.events.length > this.maxQueueSize) {
            this.events.splice(0, this.events.length - this.maxQueueSize);
        }
    }
    normalizeUserData(data) {
        const cleanedData = {};
        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                const value = data[key];
                if (Array.isArray(value)) {
                    cleanedData[key] = value.join(', ');
                }
                else {
                    cleanedData[key] = value;
                }
            }
        }
        return cleanedData;
    }
    setUser(email, data) {
        const wasAuthenticated = this.isAuthenticated;
        const previousId = this.anonymousId;
        const normalizedData = this.normalizeUserData(data);
        this.email = email;
        this.data = normalizedData;
        this.isAuthenticated = true;
        if (!wasAuthenticated && (previousId != null || previousId !== '')) {
            this.addEvent(analytics_collector_1.AnalyticEventType.SetAlias, {
                alias: previousId,
                distinct_id: email,
                previous_id: previousId,
                $anonymous_id: previousId
            }, '$create_alias', previousId);
        }
        const setData = { email, ...normalizedData };
        this.addEvent(analytics_collector_1.AnalyticEventType.SetUser, {
            $set: setData,
            $set_once: {
                initial_url: window.location.pathname,
                first_seen: new Date().toISOString()
            }
        }, '$identify');
    }
    setAlias(distinctId, alias) {
        this.addEvent(analytics_collector_1.AnalyticEventType.SetAlias, {
            alias,
            previous_id: distinctId
        }, '$create_alias');
    }
    setTag(key, value) {
        this.addEvent(analytics_collector_1.AnalyticEventType.SetTag, {
            $set: { [key]: value },
            $set_once: { initial_url: window.location.pathname }
        }, '$set');
    }
    setWorkspace(ws, guest) {
        const prop = guest ? 'visited-workspace' : 'workspace';
        this.addEvent(analytics_collector_1.AnalyticEventType.SetGroup, {
            $group_0: ws,
            $group_key: ws,
            $group_set: {
                name: ws,
                joined_at: new Date().toISOString()
            },
            $group_type: prop,
            $groups: { [prop]: ws }
        }, '$groupidentify');
    }
    handleEvent(event, params) {
        this.addEvent(analytics_collector_1.AnalyticEventType.CustomEvent, { event, ...params }, event);
    }
    handleMetric(name, value, labels) {
        const key = `${name}\u0000${JSON.stringify(labels ?? {})}`;
        const bucket = this.metricBuckets.get(key);
        if (bucket === undefined) {
            this.metricBuckets.set(key, { metric: name, labels: labels ?? {}, count: 1, sum: value, min: value, max: value });
            return;
        }
        bucket.count++;
        bucket.sum += value;
        if (value < bucket.min)
            bucket.min = value;
        if (value > bucket.max)
            bucket.max = value;
    }
    drainMetrics() {
        for (const b of this.metricBuckets.values()) {
            this.addEvent(analytics_collector_1.AnalyticEventType.Metric, { metric: b.metric, value: b.sum, count: b.count, min: b.min, max: b.max, labels: b.labels }, b.metric);
        }
        this.metricBuckets.clear();
    }
    handleError(error) {
        const currentId = this.isAuthenticated && (this.email != null || this.email !== '') ? this.email : this.anonymousId;
        const event = {
            event: analytics_collector_1.AnalyticEventType.Error,
            properties: {
                error_message: error.message ?? 'Unknown error',
                error_type: error.name ?? 'Error',
                error_stack: error.stack ?? ''
            },
            timestamp: Date.now(),
            distinct_id: currentId ?? ''
        };
        this.events.push(event);
        // We need to trigger sending immediately for errors
        void this.sendEvents();
    }
    navigate(path) {
        (0, utils_1.triggerUrlChange)();
        this.addEvent(analytics_collector_1.AnalyticEventType.Navigation, { path }, '$pageview');
    }
    logout() {
        void this.sendEvents();
        this.email = undefined;
        this.isAuthenticated = false;
        this.data = null;
        this.anonymousId = this.generateAnonymousId();
    }
    destroy() {
        this.stopCollectionTimer();
        void this.sendEvents();
    }
    flush() {
        return this.sendEvents();
    }
}
exports.AnalyticsCollectorProvider = AnalyticsCollectorProvider;
//# sourceMappingURL=analyticsCollector.js.map